<template>
  <div class="vz-align-center" ref="$el">
    <div class="vz-direction-calendar-label flex flex-space-between" :class="{'color-inactive': disabled}">
      <span>Дата</span>
      <span class="vz-cursor-pointer hovered-primary"
        @click="onSwapDisplay"
      >
          {{additionalLabel}}
      </span>
    </div>
    <div class="vz-direction-calendar" v-if="!isDatepicker">
      <div class="vz-direction-calendar-arrow" @click="onArrowClick(-100)" :class="{inactive: inActiveLeftArrow}">
        <span class="mdi mdi-chevron-left" />
      </div>
      <div class="vz-direction-calendar-calendar" ref="calendar">
        <div class="vz-direction-calendar-calendar-month">
          {{ getMonth }}
        </div>
        <div ref="calendarSlider" class="vz-direction-calendar-calendar-slider" @mousewheel.prevent="onMouseWheel" @scroll="onScroll">
          <div
            v-for="(date, index) of dates"
            :key="date.date"
            ref="day"
            class="vz-direction-calendar-calendar-slider-day"
            :title="getTitleDay(date)"
            :data-text="getDay(date)"
            :data-date="date.date"
            :class="{
              active: date.isDefault,
              disabled: date.isDisabled || disabled,
              discount: date.isDiscount && !disabled,
              express: date.isExpress && !disabled,
              action: date.isAction && !disabled,
              faster: date.isFaster && !disabled,
              warehousing: date.hasPaidWarehousing && !disabled,
              mark: date.mark && !disabled,
            }"
            @click.prevent="onClickDay(date, index)"
          >
            <div class="vz-direction-calendar-calendar-slider-day-num">
              {{ getDay(date) }}
            </div>
            <div class="vz-direction-calendar-calendar-slider-day-weekday">
              {{ getWeekday(date) }}
            </div>
            <template v-if="!disabled">
              <span v-if="date.isAction" class="action">акция</span>
              <span v-if="date.isExpress" class="express">экспресс</span>
              <span v-if="date.isFaster" class="faster">быстро</span>
              <span v-if="date.isDiscount" class="discount">выгодно</span>
              <span v-if="date.hasPaidWarehousing" class="warehousing">хранение</span>
            </template>
          </div>

          <div class="vz-direction-calendar-calendar" />
        </div>
      </div>
      <div class="vz-direction-calendar-arrow right" @click="onArrowClick(100)" :class="{inactive: inActiveRightArrow}">
        <span class="mdi mdi-chevron-right" />
      </div>
    </div>
    <template v-else>
      <vz-datepicker
      use-face-only
      expanded
      :is-show-controls="false"
      :custom-dates="props.dates"
      @change="onChangeDatepickerDate"
    />
    </template>
  </div>
</template>

<script setup lang="ts">
import { PropType } from "vue";
import { daysOfWeeks, monthOfYear } from "~/common/date";
import { TimetableDate } from "~/types/types";
import { useDayjs } from '#dayjs'

const emit = defineEmits(['change-day'])

const props = defineProps({
  dates:{
    type: Array as PropType<TimetableDate[]>,
    default: () => ([
      {date: '1970-01-01', isDisabled: true},
      {date: '1970-01-02', isDisabled: true},
      {date: '1970-01-03', isDisabled: true},
      {date: '1970-01-04', isDisabled: true},
      {date: '1970-01-05', isDisabled: true},
      {date: '1970-01-06', isDisabled: true},
      {date: '1970-01-07', isDisabled: true},
      {date: '1970-01-08', isDisabled: true},
    ])
  },
  date: {
    type: Object as PropType<TimetableDate>,
    default: () => null
  },
  disabled:{
    type: Boolean,
    default: false
  },
  directionTo: {
    type: Boolean,
    default: false
  }
})

const dayjs = useDayjs()
const datePickerDate = ref('')
const isDatepicker = ref(false)
const $el = ref({} as HTMLDivElement)
const calendarSlider = ref({} as HTMLDivElement)
const day = reactive<HTMLDivElement[]>([])
const monthDate = ref('')
const firstDates = ref<HTMLElement[]>([])
const calendar = ref({} as HTMLDivElement)
const inActiveLeftArrow = ref(false)
const inActiveRightArrow = ref(false)

const selectedDate = computed(() => {
  return props.dates.find(d => d.isDefault) as TimetableDate
})

watch(selectedDate,
  async () => {
    monthDate.value = selectedDate.value?.date || ''
    await nextTick()
    const day = findSeletedDate()
    if ( day ) {
      scrollDayToCenter(day)
      onScroll()
    }
  },
  { immediate: true }
)

watch( () => props.dates, () => {
  selecteDatepickerDate()
}, {immediate: true})

onMounted((): void => {
    setFirstDates()
    onScroll()
})

const additionalLabel = computed( () => {
  return isDatepicker.value ? 'Скрыть календарь' : 'Календарь'
})

const getMonth = computed(():string => {
  if (selectedDate.value) {
    const monthIndex = dayjs(monthDate.value).month()
    return monthOfYear[monthIndex]?.long?.toLowerCase()
  }
  return ''
})

function setFirstDates(): void {
  firstDates.value = Array.from(calendarSlider.value?.querySelectorAll('[data-text="01"]') ||
    calendarSlider.value?.querySelectorAll('.vz-direction-calendar-calendar-slider-day') || [])
}

function getTitleDay(date: TimetableDate): string {
  if (date.isFaster) {
    return 'Быстро'
  }
  return date.isDiscount ? 'Выгодно' : date.isExpress ? 'Экспресс' : date.hasPaidWarehousing ? 'Храненине' : date.isAction ? 'Акция' : ''
}

function onMouseWheel({ deltaY }: WheelEvent): void {
  calendarSlider.value.scrollLeft += deltaY
}

function getDay({ date }: {date: string}): string {
  return dayjs(date).format('DD')
}

function getWeekday({ date }: {date: string}): string {
  const day = dayjs(date).day()
  return daysOfWeeks[day]?.short || ''
}

function onArrowClick(deltaY: number): void {
  calendarSlider.value.scrollLeft += deltaY
}

function onClickDay(date: TimetableDate, index: number): void {
  if (date.isDisabled || date.isDefault || props.disabled) { return }
  scrollDayToCenter(day[index])
  $el.value.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
  emit('change-day', date)
}

function onScroll(): void {
  const parentRect = calendarSlider.value?.getBoundingClientRect?.()
  const firstDay = firstDates.value?.find(fDay => (fDay as HTMLElement).getBoundingClientRect().left > parentRect.left) as HTMLElement
  const dayRect = firstDay?.getBoundingClientRect()
  const dayLeft = dayRect?.left - parentRect?.left

  inActiveLeftArrow.value = calendarSlider.value.scrollLeft === 0
  inActiveRightArrow.value = calendarSlider.value.scrollWidth - calendarSlider.value.scrollLeft === parentRect.width;

  if (!firstDay || !parentRect) {
    return
  }

  if (dayLeft < parentRect.width / 2) {
    monthDate.value = firstDay.dataset.date || ''
  } else {
    monthDate.value = dayjs(firstDay.dataset.date).subtract(1, 'day').format('YYYY-MM-DD')
  }

}

function findSeletedDate(): HTMLElement | null {
  const day = calendarSlider.value?.querySelector?.('.vz-direction-calendar-calendar-slider-day.active')
  return day as HTMLElement
}

function scrollDayToCenter(day: HTMLElement | null): void {
  if (day) {
    const dayRect = day.getBoundingClientRect()
    const parentRect = calendarSlider.value.getBoundingClientRect()
    const dayLeft = dayRect.left - parentRect.left
    const offset = ((parentRect.width - dayRect.width) / 2)
    calendarSlider.value.scrollLeft = calendarSlider.value.scrollLeft + (dayLeft - offset)
  }
}

function onSwapDisplay() {
  isDatepicker.value = !isDatepicker.value
}

function onChangeDatepickerDate(date: string) {
  if ( props.disabled) { return }

  const selectedDate = props.dates.find( d => d.date === date)

  if ( selectedDate ) {
    $el.value.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
    emit('change-day', selectedDate)
  }
}

function selecteDatepickerDate() {
  const date  = props.dates.find( d => d.isDefault)
  if ( date ) {
    datePickerDate.value = date.date
  }
}
</script>
